import { Link } from "react-router-dom";
import { SquarePen } from "lucide-react";

import { Button } from "@/components/ui/button";
import ACL_RELATIONSHIPS from "acl-relationships";
import CopyableValue from "components/CopyableValue";
import EntityCampaignsPanel from "components/sidebar/sections/EntityCampaignsPanel";
import {
  CustomFieldsPanelSection,
  PanelField,
  PanelSection,
} from "components/sidebar/sections/InfoPanel";
import { PanelSectionSkeleton } from "components/sidebar/sections/PanelSectionSkeleton";
import ApplyTagsDropdown from "components/tags/ApplyTagsDropdown";
import TagList from "components/TagList";
import { Tooltip, TooltipTrigger, TooltipContent } from "components/ui/tooltip";
import { formatAddress } from "helpers/address";
import useAuthorized from "hooks/useAuthorized";
import { useEntityNavigation } from "routes/entity/useEntityNavigation";

const EDIT_ACLS = [ACL_RELATIONSHIPS.entity.edit, ACL_RELATIONSHIPS.user.read];

// The entity summary shown inside another model's detail rail, such as a contact
// or a case: the entity's name, tags, key fields, custom fields, and campaigns.
// Case rails also list the entity's addresses and offer an entity edit.
const EntitySection = ({
  entity,
  showDisabledTags,
  onUpdate,
  showAddresses = false,
  showEdit = false,
  isLoading = false,
}) => {
  const canEdit = useAuthorized(EDIT_ACLS);
  const { openEdit } = useEntityNavigation();

  // Every part of the section reads the entity, which is not there yet.
  if (isLoading) {
    return (
      <>
        <PanelSectionSkeleton />
        <PanelSectionSkeleton />
      </>
    );
  }

  return (
    <>
      <PanelSection
        title="Entity"
        action={
          showEdit &&
          canEdit && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="outline"
                    size="icon-xs"
                    aria-label="Edit"
                    onClick={() => openEdit(entity.id)}
                  >
                    <SquarePen />
                  </Button>
                }
              />
              <TooltipContent>Edit</TooltipContent>
            </Tooltip>
          )
        }
      >
        <Link
          to={`/entities/${entity.id}`}
          className="tw:font-heading tw:text-xs tw:font-semibold tw:text-sidebar-foreground! tw:hover:underline"
        >
          {entity.name}
        </Link>
        <PanelField
          label="Entity Tags"
          action={
            <ApplyTagsDropdown
              modelType="Entity"
              rows={[entity]}
              checkedArr={[entity.id]}
              onSuccess={onUpdate}
              showDisabledTags={showDisabledTags}
              iconOnly
              align="end"
            />
          }
        >
          <TagList
            tags={entity.tags}
            modelType="Entity"
            modelId={entity.id}
            onUpdate={onUpdate}
          />
        </PanelField>
        <PanelField label="Website">
          {entity.url ? <CopyableValue value={entity.url} /> : null}
        </PanelField>
        <PanelField label="Account Manager">
          {entity.account_managers?.map(({ user_full_name }) => (
            <span key={user_full_name}>{user_full_name}</span>
          ))}
        </PanelField>
        {showAddresses && (
          <PanelField label="Addresses">
            {entity.addresses?.map(address => (
              <CopyableValue key={address.id} value={formatAddress(address)} />
            ))}
          </PanelField>
        )}
      </PanelSection>

      <CustomFieldsPanelSection
        title="Entity Custom Fields"
        items={entity.custom_fields}
        modelType="Entity"
      />

      <EntityCampaignsPanel entityId={entity.id} />
    </>
  );
};

export default EntitySection;
